import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Radio, MapPin, Navigation, Zap, Timer, Clock, CalendarClock, Repeat,
  PackageCheck, Building2, ShieldCheck, Phone, Mail, LogIn,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const HERO_IMG = "https://images.unsplash.com/photo-1667216291821-001933f54097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxCcml0aXNoJTIwQ29sdW1iaWElMjBtb3VudGFpbiUyMGhpZ2h3YXklMjBmb3Jlc3R8ZW58MHx8fHwxNzgyODkwMjk4fDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { id: "direct", icon: Navigation, name: "Direct", desc: "Dedicated point-to-point delivery with no additional stops — our fastest service tier." },
  { id: "hot", icon: Zap, name: "HOT", desc: "Emergency priority dispatch for time-critical shipments requiring immediate driver assignment." },
  { id: "rush", icon: Timer, name: "Rush", desc: "Expedited pickup and delivery completed within hours of booking." },
  { id: "same", icon: Clock, name: "Same Day", desc: "Reliable same-business-day delivery with full tracking from pickup to drop-off." },
  { id: "next", icon: CalendarClock, name: "Next Day", desc: "Guaranteed next-business-day delivery for planned, cost-effective shipping." },
  { id: "regular", icon: Repeat, name: "Scheduled & Recurring", desc: "Routine and recurring routes tailored to businesses with regular shipping needs." },
];

const CAPABILITIES = [
  { id: "live", icon: Radio, label: "Online Tracking", desc: "Monitor every shipment online from booking through to delivery with status updates at each stage." },
  { id: "proof", icon: PackageCheck, label: "Proof of Delivery", desc: "Signature, recipient name and delivery photo captured and stored against every completed job." },
  { id: "corporate", icon: Building2, label: "Corporate Accounts", desc: "Account-based billing with transparent breakdowns of base rate, surcharges, wait time and GST." },
  { id: "coverage", icon: MapPin, label: "Lower Mainland Coverage", desc: "Dedicated service across the Lower Mainland, from downtown Vancouver through the Fraser Valley." },
];

export default function Landing() {
  const navigate = useNavigate();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen grain relative overflow-hidden">
      {/* Top bar */}
      <header className="border-b border-border/70 px-6 md:px-12 py-4 flex items-center justify-between relative z-20 bg-background/80 backdrop-blur sticky top-0">
        <div className="flex items-center gap-3">
          <BrandLogo className="h-9" />
          <div>
            <div className="font-display text-lg md:text-xl tracking-tight leading-none">COUNTRY COURIER</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-brand-green mt-1">Driven to Deliver</div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <button onClick={() => scrollTo("services")} className="hover:text-foreground transition" data-testid="nav-services">Services</button>
          <button onClick={() => scrollTo("why")} className="hover:text-foreground transition" data-testid="nav-why">Why Us</button>
          <button onClick={() => scrollTo("contact")} className="hover:text-foreground transition" data-testid="nav-contact">Contact</button>
        </nav>

        <Button onClick={() => navigate("/login")} className="font-mono uppercase text-xs bg-brand-green text-black hover:bg-brand-green/90 font-bold h-10 px-5" data-testid="login-menu-btn">
          <LogIn className="w-4 h-4 mr-2" /> Login
        </Button>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="British Columbia mountain highway" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(7,16,30,0.97) 0%, rgba(7,16,30,0.85) 45%, rgba(7,16,30,0.55) 100%)" }} />
        </div>
        <div className="relative z-10 px-6 md:px-12 pt-20 md:pt-28 pb-20 md:pb-32 max-w-4xl">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-brand-green flex items-center gap-2 mb-6">
            <Radio className="w-3 h-3 animate-pulse" /> Lower Mainland · British Columbia
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-display leading-[0.9] uppercase tracking-tighter mb-6 text-white">
            Driven to<br />
            <span className="text-brand-green">deliver.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mb-8">
            Country Courier is a Lower Mainland delivery partner offering same-day, rush and scheduled
            courier services, backed by online tracking and digital proof of delivery on every shipment.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => scrollTo("services")} className="h-14 px-8 bg-brand-green text-white hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold" data-testid="hero-services-btn">
              Our Services <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => scrollTo("contact")} variant="outline" className="h-14 px-8 font-mono uppercase tracking-wider font-bold border-2 border-white/70 text-white bg-white/10 hover:bg-white/20 hover:text-white" data-testid="hero-contact-btn">
              Get in Touch
            </Button>
          </div>
        </div>
      </section>

      <div className="tape-stripe h-3 w-full relative z-10" />

      {/* Services */}
      <section id="services" className="px-6 md:px-12 py-16 md:py-24 relative z-10">
        <div className="max-w-3xl mb-12">
          <div className="h-1 w-14 bg-brand-green rounded-full mb-6" />
          <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter mb-4">Service tiers for every shipment</h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Choose the delivery speed that fits your shipment. Every tier is dispatched, tracked and
            documented through the same dispatch operations platform.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {SERVICES.map((s) => (
            <div key={s.name} className="bg-card p-8 hover:bg-muted transition group" data-testid={`service-${s.id}`}>
              <s.icon className="w-8 h-8 text-brand-green mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <div className="font-display text-2xl uppercase mb-2 tracking-tight">{s.name}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Why us / capabilities */}
      <section id="why" className="border-t border-border px-6 md:px-12 py-16 md:py-24 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <div className="h-1 w-14 bg-brand-green rounded-full mb-6" />
            <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter mb-4">Local fleet.<br />Real-time control.</h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Our drivers know the Lower Mainland and our dispatch team monitors every active route,
              so your shipment stays visible and accountable from pickup to delivery.
            </p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-border">
            {CAPABILITIES.map((c) => (
              <div key={c.label} className="bg-card p-8" data-testid={`capability-${c.id}`}>
                <c.icon className="w-7 h-7 text-brand-green mb-4" strokeWidth={1.5} />
                <div className="font-display text-xl uppercase mb-2 tracking-tight">{c.label}</div>
                <div className="text-sm text-muted-foreground leading-relaxed">{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="border-t border-border px-6 md:px-12 py-16 md:py-24 relative z-10 bg-card">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="h-1 w-14 bg-brand-green rounded-full mb-6" />
            <h2 className="text-4xl md:text-5xl font-display uppercase tracking-tighter mb-6">Ready to ship?</h2>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-lg mb-8">
              Open a corporate account or request a one-off delivery. Our dispatch team will have your
              shipment moving across the Lower Mainland.
            </p>
            <div className="space-y-3 font-mono text-sm">
              <a href="tel:+16047869572" className="flex items-center gap-3 hover:text-brand-green transition" data-testid="contact-phone"><Phone className="w-4 h-4 text-brand-green" /> 604-786-9572</a>
              <a href="mailto:countrycourier@outlook.com" className="flex items-center gap-3 hover:text-brand-green transition" data-testid="contact-email"><Mail className="w-4 h-4 text-brand-green" /> countrycourier@outlook.com</a>
              <div className="flex items-center gap-3 text-muted-foreground"><MapPin className="w-4 h-4 text-brand-green" /> Serving the Lower Mainland, British Columbia</div>
            </div>
          </div>
          <div className="border-2 border-border p-8 md:p-10 brutalist-shadow bg-background">
            <ShieldCheck className="w-10 h-10 text-brand-green mb-5" strokeWidth={1.5} />
            <div className="font-display text-2xl md:text-3xl uppercase tracking-tight mb-3">Already with us?</div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Sign in to track your shipments, view invoices and manage your deliveries.
            </p>
            <Button onClick={() => navigate("/login")} className="w-full h-14 bg-brand-green text-black hover:bg-brand-green/90 font-mono uppercase tracking-wider font-bold" data-testid="contact-login-btn">
              <LogIn className="w-4 h-4 mr-2" /> Login
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border px-6 md:px-12 py-6 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground flex flex-col md:flex-row gap-2 justify-between relative z-10">
        <div>© {new Date().getFullYear()} Country Courier — Driven to Deliver.</div>
        <div>British Columbia // Lower Mainland</div>
      </footer>
    </div>
  );
}
